'use client'

import React from 'react'

const HowToGetHexagonalFormation = () => {
  // Dimensiones del hexágono regular
  const sideLength = 120 // px
  const hexagonWidth = sideLength * Math.sqrt(3) // 207.84px
  const hexagonHeight = sideLength * 2 // 240px
  const margin = sideLength / 4 // 30px
  
  // Coordenadas para formación hexagonal (4 hexágonos principales)
  const hexagonPositions = [
    { id: 1, title: 'Desde Bogotá', description: 'Autopista Norte - 45 minutos', top: 0, left: 0, type: 'main' },
    { id: 2, title: 'Llegar a Tocancipá', description: 'Salida al pueblo', top: hexagonHeight * 0.75 + margin, left: hexagonWidth * 0.5 + margin, type: 'main' },
    { id: 3, title: 'Vía rural', description: '20 minutos señalizados', top: hexagonHeight * 1.5 + (margin * 2), left: 0, type: 'main' },
    { id: 4, title: 'Destino final', description: 'Parapente El Dorado', top: hexagonHeight * 2.25 + (margin * 3), left: hexagonWidth * 0.5 + margin, type: 'main' }
  ]

  // Hexágonos decorativos adicionales (patrón extendido)
  const decorativeHexagons = [
    // Fila superior izquierda
    { top: -hexagonHeight * 0.5, left: -hexagonWidth * 0.5 },
    { top: -hexagonHeight * 0.25, left: -hexagonWidth * 0.25 },
    { top: 0, left: -hexagonWidth * 0.75 },
    // Fila superior derecha
    { top: -hexagonHeight * 0.5, left: hexagonWidth * 1.25 },
    { top: -hexagonHeight * 0.25, left: hexagonWidth * 1.5 },
    { top: 0, left: hexagonWidth * 1.75 },
    // Lado izquierdo
    { top: hexagonHeight * 0.5, left: -hexagonWidth * 0.6 },
    { top: hexagonHeight * 1.0, left: -hexagonWidth * 0.8 },
    { top: hexagonHeight * 1.5, left: -hexagonWidth * 0.6 },
    // Lado derecho
    { top: hexagonHeight * 0.5, left: hexagonWidth * 1.6 },
    { top: hexagonHeight * 1.0, left: hexagonWidth * 1.8 },
    { top: hexagonHeight * 1.5, left: hexagonWidth * 1.6 },
    // Inferior izquierda
    { top: hexagonHeight * 2.5, left: -hexagonWidth * 0.5 },
    { top: hexagonHeight * 2.75, left: -hexagonWidth * 0.25 },
    { top: hexagonHeight * 3.0, left: -hexagonWidth * 0.75 },
    // Inferior derecha
    { top: hexagonHeight * 2.5, left: hexagonWidth * 1.25 },
    { top: hexagonHeight * 2.75, left: hexagonWidth * 1.5 },
    { top: hexagonHeight * 3.0, left: hexagonWidth * 1.75 },
  ]

  // Componente Hexágono
  const Hexagon = ({ hexagon, isDecorative = false }: { hexagon: any, isDecorative?: boolean }) => {
    return (
      <div 
        className="absolute group"
        style={{
          top: `${hexagon.top}px`,
          left: `${hexagon.left}px`,
          width: `${hexagonWidth}px`,
          height: `${hexagonHeight}px`
        }}
      >
        <div 
          className="relative w-full h-full"
          style={{
            clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)'
          }}
        >
          <div className={`absolute inset-0 backdrop-blur-lg ${isDecorative ? 'bg-white/5' : 'bg-white/15'}`}></div>
          
          {/* Borde: Naranja para principales, Blanco para decorativos */}
          <div 
            className="absolute inset-0"
            style={{
              clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
              border: isDecorative ? '2px solid rgba(255,255,255,0.3)' : '3px solid rgba(251,146,60,0.8)',
              boxShadow: isDecorative ? 'none' : '0 0 15px rgba(251,146,60,0.5)'
            }}
          ></div>
          
          {/* Contenido (solo para hexágonos principales) */}
          {!isDecorative && (
            <div className="relative z-10 flex flex-col items-center justify-center h-full p-8">
              <h3 className="text-2xl font-bold font-bisteron text-white text-center mb-4 drop-shadow-[0_0_6px_rgba(0,0,0,1)]">
                {hexagon.title}
              </h3>
              <p className="text-lg text-white text-center leading-tight drop-shadow-[0_0_4px_rgba(0,0,0,1)]">
                {hexagon.description}
              </p>
              <div className="absolute bottom-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <svg className="w-6 h-6 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              </div>
            </div>
          )}
          
          {/* Efecto de brillo al hover (solo principales) */}
          {!isDecorative && (
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/20 to-transparent"></div>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <section id="como-llegar-hexagonal" className="relative py-16 md:py-24 overflow-hidden">
      <div className="absolute inset-0 z-0 bg-black/50"></div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Título */}
        <div className="text-center mb-16 relative z-20">
          <div className="inline-block relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-blue-500 to-purple-500 rounded-3xl blur-xl opacity-20"></div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bisteron font-bold relative z-10 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
              <span className="text-white">¿CÓMO </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
                LLEGAR
              </span>
              <span className="text-white">?</span>
            </h2>
          </div>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto mt-6 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
            Los 4 pasos en formación hexagonal para llegar desde Bogotá a nuestro parapuerto
          </p>
        </div>

        {/* Contenedor de la formación hexagonal */}
        <div className="flex justify-center relative">
          <div 
            className="relative"
            style={{
              width: `${hexagonWidth * 2.5 + margin * 2}px`,
              height: `${hexagonHeight * 3.5 + margin * 4}px`
            }}
          >
            {/* Líneas de conexión entre hexágonos principales */}
            <svg className="absolute inset-0 w-full h-full" style={{ zIndex: 5 }}>
              <line x1={hexagonWidth * 0.5} y1={hexagonHeight * 0.5} x2={hexagonWidth * 0.5 + margin + hexagonWidth * 0.5} y2={hexagonHeight * 0.75 + margin + hexagonHeight * 0.5} stroke="white" strokeWidth="2" strokeDasharray="8,8" opacity="0.6" />
              <line x1={hexagonWidth * 0.5 + margin + hexagonWidth * 0.5} y1={hexagonHeight * 0.75 + margin + hexagonHeight * 0.5} x2={hexagonWidth * 0.5} y2={hexagonHeight * 1.5 + (margin * 2) + hexagonHeight * 0.5} stroke="white" strokeWidth="2" strokeDasharray="8,8" opacity="0.6" />
              <line x1={hexagonWidth * 0.5} y1={hexagonHeight * 1.5 + (margin * 2) + hexagonHeight * 0.5} x2={hexagonWidth * 0.5 + margin + hexagonWidth * 0.5} y2={hexagonHeight * 2.25 + (margin * 3) + hexagonHeight * 0.5} stroke="white" strokeWidth="2" strokeDasharray="8,8" opacity="0.6" />
            </svg>

            {/* Hexágonos decorativos (patrón de fondo) */}
            {decorativeHexagons.map((hex, index) => (
              <Hexagon key={`decorative-${index}`} hexagon={{...hex, type: 'decorative'}} isDecorative={true} />
            ))}

            {/* Hexágonos principales */}
            {hexagonPositions.map(hexagon => (
              <div key={hexagon.id} className="group cursor-pointer" style={{ zIndex: 10 }}>
                <Hexagon hexagon={hexagon} isDecorative={false} />
              </div>
            ))}
          </div>
        </div>

        {/* Espacio para separar */}
        <div className="mt-60"></div>
        
        {/* Link de Waze */}
        <div className="max-w-md mx-auto relative z-20">
          <div className="text-center">
            <a
              href="https://waze.com/ul/hd2gkn61x6"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-10 py-5 bg-[#33CCFF] text-white rounded-3xl font-bold 
                hover:bg-[#2AA3D6] transition-colors shadow-2xl text-xl hover:scale-105 duration-300"
            >
              <svg className="w-8 h-8 mr-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
              ABRIR EN WAZE
            </a>
            <p className="text-white/80 text-sm mt-4 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
              Haz click para abrir la ruta exacta en la app de Waze
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}

export default HowToGetHexagonalFormation
